<script>
import baseMixin from '@/mixins/baseMixin'
import dayjs from 'dayjs'
import DateFormatToken from '@/constants/date/DateFormatToken'
import { mapActions, mapGetters } from 'vuex'
import LocationSelector from '@components/user/location-selector.vue'
import { DurationUnits } from '@/constants/date/DurationUnits'
import { isNullOrEmptyArray } from '@helpers/array-helpers'
import RestrictionType from '@/constants/locationRestriction/RestrictionType'
import LocationRestriction from '@/models/locationRestriction/locationRestriction'
import AdminSelector from '@components/user/admin-selector.vue'
import NewRestrictionCreatorConfirmation from '@components/locationRestrictions/new-restriction-creator-confirm.vue'

export default {
  name: 'NewRestrictionCreator',
  components: {
    NewRestrictionCreatorConfirmation,
    AdminSelector,
    LocationSelector,
  },
  mixins: [baseMixin],
  model: {
    prop: 'adminIds',
    event: 'change',
  },
  props: {
    adminIds: {
      type: Array,
      default: () => [],
    },
  },
  data: () => {
    return {
      isSaving: false,
      isFormShowing: false,
      adminId: null,
      isDateSelectorShowing: false,
      dateRange: [
        dayjs().add(1, DurationUnits.DAY).format(DateFormatToken.YYYYMMDD),
      ],
      message: '',
      isCustomMessage: false,
      selectedLocations: [],
      isConfirmDialogOpen: false,
    }
  },
  computed: {
    ...mapGetters({
      getCurrentUserTeams: 'auth/currentUserTeams',
      getCurrentUserDivision: 'auth/currentUserDivision',
      getViewableAdminIds: 'locationRestriction/getViewableAdminIds',
    }),
    messageCharLimit: () => 500,
    selectedLocationIds() {
      return this.selectedLocations.map((l) => l.id)
    },
    sortedDateRange() {
      return this.dateRange.map((d) => dayjs(d)).sort((d1, d2) => d1 - d2)
    },
    dateRangeInputText() {
      const formattedRange = this.sortedDateRange.map((d) =>
        dayjs(d).locale(this.$i18n.locale).format(DateFormatToken.LL)
      )
      return formattedRange.join(' to ')
    },
    adminIdsVModel: {
      get() {
        return this.adminIds
      },
      set(val) {
        this.$emit('change', val)
      },
    },
    form() {
      return {
        restriction: RestrictionType.BOOKING_CREATE,
        ids: this.selectedLocationIds,
        from: this.sortedDateRange[0],
        until: this.sortedDateRange[1] ?? this.sortedDateRange[0],
        message: this.isCustomMessage ? this.message : null,
      }
    },
    isFormValid() {
      return (
        !isNullOrEmptyArray(this.selectedLocations) &&
        this.message?.length <= this.messageCharLimit &&
        this.message
      )
    },
  },
  watch: {
    isCustomMessage(updateVal) {
      if (!updateVal) this.message = this.defaultMessage()
    },
    sortedDateRange() {
      if (!this.isCustomMessage) this.message = this.defaultMessage()
    },
  },
  created() {
    this.message = this.defaultMessage()
  },
  methods: {
    ...mapActions({
      addRestrictionsByLocationIds:
        'locationRestriction/createRestrictionByLocationIds',
    }),
    resetForm() {
      // Reset the form
      this.$refs.locationSelector.clearSelection()
      this.isConfirmDialogOpen = false
      this.isFormShowing = false
      this.selectedLocations = []
      this.dateRange = [
        dayjs().add(1, DurationUnits.DAY).format(DateFormatToken.YYYYMMDD),
      ]
      this.message = ''
      this.isCustomMessage = false
    },
    defaultMessage() {
      return LocationRestriction.createDefaultMessage(this.form.until)
    },
    allowedDates(date) {
      return dayjs(date) >= dayjs()
    },
    confirmSelection() {
      this.isConfirmDialogOpen = true
    },
    async saveSelection() {
      this.isSaving = true
      const form = {
        restriction: RestrictionType.BOOKING_CREATE,
        ids: this.selectedLocationIds,
        from: this.sortedDateRange[0],
        until: this.sortedDateRange[1] ?? this.sortedDateRange[0],
        message: this.isCustomMessage ? this.message : this.defaultMessage(),
      }

      const response = await this.addRestrictionsByLocationIds(form)
      if (response.isSuccess) {
        this.$emit('saved', response.data)
        this.resetForm()
      }
      this.isSaving = false
    },
  },
}
</script>

<template>
  <v-dialog v-model="isFormShowing" width="600">
    <template v-slot:activator="{ on, attrs }">
      <v-btn
        color="primary"
        v-bind="attrs"
        data-testid="open-form-btn"
        v-on="on"
      >
        {{ $t('locationRestrictions.create.openBtn') }}
      </v-btn>
    </template>

    <v-card :loading="isSaving">
      <v-form data-testid="restriction-form">
        <v-container>
          <div
            class="text-h5 px-0 py-2 d-flex justify-space-between align-center"
          >
            {{ $t('locationRestrictions.create.title') }}
            <v-btn icon @click="isFormShowing = false"
              ><v-icon>mdi-close</v-icon></v-btn
            >
          </div>
          <AdminSelector
            v-model="adminIdsVModel"
            :allowed-admin-ids="getViewableAdminIds"
          />
          <LocationSelector
            ref="locationSelector"
            data-testid="location-selector"
            :admin-ids="adminIds"
            @save="selectedLocations = $event"
          />

          <v-menu
            ref="menu"
            v-model="isDateSelectorShowing"
            :close-on-content-click="false"
            :return-value.sync="dateRange"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="dateRangeInputText"
                data-testid="date-input-text"
                :label="$t('locationRestrictions.create.dateInputLabel')"
                prepend-icon="mdi-calendar"
                readonly
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              id="dateRangePicker"
              v-model="dateRange"
              data-testid="date-range-picker"
              range
              no-title
              scrollable
              :allowed-dates="allowedDates"
            >
              <v-btn text @click="isDateSelectorShowing = false">
                {{ $t('locationRestrictions.create.dateCancelBtn') }}
              </v-btn>
              <v-spacer />
              <v-btn text color="primary" @click="$refs.menu.save(dateRange)">
                {{ $t('locationRestrictions.create.dateOkBtn') }}
              </v-btn>
            </v-date-picker>
          </v-menu>

          <v-checkbox
            v-model="isCustomMessage"
            data-testid="message-checkbox"
            :label="$t('locationRestrictions.create.customMessageCheckbox')"
          ></v-checkbox>
          <v-textarea
            v-model="message"
            data-testid="message-textarea"
            :disabled="!isCustomMessage"
            clearable
            clear-icon="mdi-close-circle"
            :label="$t('locationRestrictions.create.messageLabel')"
            :counter="messageCharLimit"
          ></v-textarea>
        </v-container>
        <v-divider></v-divider>

        <v-card-actions class="d-flex justify-end">
          <NewRestrictionCreatorConfirmation
            v-model="isConfirmDialogOpen"
            :loading="isSaving"
            :selected-locations="selectedLocations"
            :message="message"
            :from="sortedDateRange[0]"
            :until="sortedDateRange[1]"
            @save="saveSelection"
          />
          <v-btn text @click="isFormShowing = false">
            {{ $t('locationRestrictions.create.cancelBtn') }}
          </v-btn>
          <v-spacer />
          <v-btn
            id="openConfirmBtn"
            color="primary"
            data-testid="open-confirm-btn"
            text
            :disabled="!isFormValid"
            @click="confirmSelection"
          >
            {{ $t('locationRestrictions.create.saveBtn') }}
          </v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>
</template>
